// @file Composable for managing emoji picker functionality
import { trackEvent } from '@@/bits/analytics'
import { emojiCategories } from '@@/bits/emoji'
import { np__, __ } from '@@/bits/intl'
import { useEmojisStore } from '@@/pinia/emojis_store'
import { useRecentEmojisStore } from '@@/pinia/recent_emojis_store'
import type { Emoji as EmojiType } from '@padlet/arvo'
import { debounce } from 'es-toolkit'
import { storeToRefs } from 'pinia'
import type { ComputedRef, Ref } from 'vue'
import { computed, nextTick, onBeforeMount, readonly, ref, shallowRef } from 'vue'

const normalizeString = (str: string): string => {
  return str.trim().toLowerCase().normalize('NFKC')
}

interface UseEmojiPickerOptions {
  recentsCategoryEnabled?: boolean
  maxRecentEmojis?: Ref<number>
  shouldShowAllEmojis?: boolean
  trackingContext?: string
  onPick?: (emojiUrl: string) => Promise<void> | void
  shouldAddToRecents?: (emoji: EmojiType) => boolean
}

interface UseEmojiPickerReturn {
  // Fetching
  isLoading: Readonly<Ref<boolean>>

  // Emoji categories
  displayedCategories: ComputedRef<string[]>
  switchCategory: (key: string) => void
  setCurrentCategoryIndex: (index: number) => void

  // Searching
  emojiSearchQuery: Ref<string>
  isSearchInputFocused: Ref<boolean>
  isInSearchMode: Readonly<Ref<boolean>>
  currentCategoryIndex: Readonly<Ref<number>>
  ariaLiveMessage: Readonly<Ref<string | undefined>>
  xNoEmojiFound: ComputedRef<boolean>
  currentShownEmojis: ComputedRef<EmojiType[]>
  debouncedSearch: (e: InputEvent) => void
  resetSearch: () => void

  // Actions
  handleEmojiPick: (emojiUrl: string) => void
}

const useEmojiPicker = (options: UseEmojiPickerOptions): UseEmojiPickerReturn => {
  const {
    recentsCategoryEnabled = false,
    maxRecentEmojis,
    shouldShowAllEmojis = false,
    trackingContext,
    onPick,
    shouldAddToRecents,
  } = options

  // #region Emoji data
  const emojisStore = useEmojisStore()
  const { isLoading, emojiData, emojiDataAsFlatArray } = storeToRefs(emojisStore)

  onBeforeMount(() => {
    void emojisStore.fetchEmojiData()
  })
  // #endregion

  // #region Emoji categories
  const recentEmojisStore = useRecentEmojisStore()
  const { addToRecents, getRecentEmojisSubset } = recentEmojisStore
  const { recentEmojis } = storeToRefs(recentEmojisStore)

  const effectiveRecentEmojis = computed(() => {
    return maxRecentEmojis ? getRecentEmojisSubset(maxRecentEmojis.value) : recentEmojis.value
  })

  const displayedCategories = computed(() =>
    // Only show the Recents category if `recentsCategoryEnabled` is true and there are recent emojis
    emojiCategories.filter(
      (category) => (recentsCategoryEnabled && effectiveRecentEmojis.value.length > 0) || category !== 'recents',
    ),
  )

  function switchCategory(category: string): void {
    resetSearch()
    currentCategoryIndex.value = displayedCategories.value.findIndex((c) => c === category)
  }

  function setCurrentCategoryIndex(index: number): void {
    if (index >= 0 && index < displayedCategories.value.length) {
      currentCategoryIndex.value = index
    }
  }
  // #endregion

  // #region Searching emojis
  const emojiSearchQuery = ref('')
  const isSearchInputFocused = ref(false)
  const isInSearchMode = ref(false)

  const currentCategoryIndex = ref(0)
  const emojiSearchResults: Ref<EmojiType[]> = shallowRef([])

  const ariaLiveMessage = ref<string | undefined>(undefined)

  const xNoEmojiFound = computed(() => emojiSearchResults.value.length === 0 && emojiSearchQuery.value.trim() !== '')

  const currentShownEmojis = computed(() => {
    if (isInSearchMode.value) return emojiSearchResults.value
    if (shouldShowAllEmojis && recentsCategoryEnabled) {
      return [effectiveRecentEmojis.value, emojiDataAsFlatArray.value].flat()
    }
    if (shouldShowAllEmojis) {
      return emojiDataAsFlatArray.value
    }
    const category = displayedCategories.value[currentCategoryIndex.value]
    if (category === 'recents' && recentsCategoryEnabled) {
      return effectiveRecentEmojis.value
    }
    return emojiData.value[category]?.emojis ?? []
  })

  function handleEmojiSearch(event: InputEvent): void {
    const emojiSearchValue = (event.target as HTMLInputElement).value
    emojiSearchQuery.value = emojiSearchValue

    if (emojiSearchQuery.value.trim() === '') {
      void resetEmojiResults()
      return
    }

    currentCategoryIndex.value = -1

    const normalizedSearchTerm = normalizeString(emojiSearchValue)
    emojiSearchResults.value = emojiDataAsFlatArray.value.filter((emoji) => {
      const indexedFields = [emoji.name, ...emoji.keywords]
        .map((field) => normalizeString(field))
        .filter((word) => word !== '')
      return indexedFields.some((word) => word.includes(normalizedSearchTerm))
    })
  }

  const debouncedSearch = debounce((e: InputEvent) => {
    isInSearchMode.value = true
    handleEmojiSearch(e)
    ariaLiveMessage.value = np__(
      'Number of emojis found',
      '%{count} emoji found.',
      '%{count} emojis found.',
      emojiSearchResults.value.length,
      { count: emojiSearchResults.value.length },
    )
    trackEvent('Emoji Picker', 'Searched', emojiSearchQuery.value, emojiSearchResults.value.length, {
      trackingContext,
    })
  }, 300)

  async function resetEmojiResults(): Promise<void> {
    ariaLiveMessage.value = __('Search reset.')
    isInSearchMode.value = false
    currentCategoryIndex.value = 0
    emojiSearchResults.value = []

    // Update ariaLiveMessage in the next tick to allow the previous message to be read
    await nextTick()
    ariaLiveMessage.value = __('Redisplaying all emojis in the panel.')
  }

  function resetSearch(): void {
    emojiSearchQuery.value = ''
    isSearchInputFocused.value = false
    isInSearchMode.value = false
    void resetEmojiResults()
  }
  // #endregion

  // #region Actions
  function handleEmojiPick(emojiUrl: string): void {
    const selectedEmoji = emojiDataAsFlatArray.value.find((emoji) => emoji.url === emojiUrl)

    if (selectedEmoji) {
      // Add to recents if recents are enabled and the emoji should be added to recents
      const shouldAdd = shouldAddToRecents ? shouldAddToRecents(selectedEmoji) : true
      if (recentsCategoryEnabled && shouldAdd) {
        addToRecents(selectedEmoji)
      }

      // Track the emoji pick
      trackEvent('Emoji Picker', 'Picked emoji', selectedEmoji.name, undefined, {
        category: selectedEmoji.category?.label,
        source: isInSearchMode.value ? 'searchAndPick' : 'browseAndPick',
        searchQuery: isInSearchMode.value ? emojiSearchQuery.value : undefined,
        trackingContext,
      })
    }

    void onPick?.(emojiUrl)
  }
  // #endregion

  return {
    // Fetching
    isLoading: readonly(isLoading),

    // Emoji categories
    displayedCategories,
    switchCategory,
    setCurrentCategoryIndex,

    // Searching
    emojiSearchQuery,
    isSearchInputFocused,
    isInSearchMode: readonly(isInSearchMode),
    currentCategoryIndex: readonly(currentCategoryIndex),
    ariaLiveMessage: readonly(ariaLiveMessage),
    xNoEmojiFound,
    currentShownEmojis,
    debouncedSearch,
    resetSearch,

    // Actions
    handleEmojiPick,
  }
}

export { useEmojiPicker }
